import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Download, Mail } from "lucide-react";
import { toast } from "sonner";

export default function AdminQuotations() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(null);
  const load = () => api.get("/quotations").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const emailQuote = async (q) => {
    const to = window.prompt("Send quotation PDF to email:", q.email || "");
    if (!to) return;
    setSending(q.id);
    try { await api.post(`/quotations/${q.id}/email`, { to }); toast.success(`Quotation emailed to ${to}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Email failed"); }
    finally { setSending(null); }
  };
  const create = async () => {
    try {
      await api.post("/quotations", editing);
      toast.success("Quote created"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };
  const download = async (id, num) => {
    const res = await api.get(`/quotations/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${num}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const addRow = () => setEditing(e => ({...e, items: [...e.items, { product_name: "", quantity: 1, unit_price: 0, discount: 0 }]}));
  const rmRow = (i) => setEditing(e => ({...e, items: e.items.filter((_, j) => j !== i)}));
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-extrabold text-2xl">Quotations</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-orange" onClick={()=>setEditing({customer_name:"", company_name:"", phone:"", email:"", items: [{product_name:"", quantity:1, unit_price:0, discount:0}], tax_rate:18, notes:"", validity_days:15})}><Plus className="w-4 h-4 mr-1"/> New Quotation</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>New Quotation</DialogTitle></DialogHeader>
            {editing && <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Customer *</Label><Input value={editing.customer_name} onChange={e=>setEditing({...editing, customer_name:e.target.value})}/></div>
                <div><Label>Company</Label><Input value={editing.company_name} onChange={e=>setEditing({...editing, company_name:e.target.value})}/></div>
                <div><Label>Phone *</Label><Input value={editing.phone} onChange={e=>setEditing({...editing, phone:e.target.value})}/></div>
                <div><Label>Email</Label><Input value={editing.email} onChange={e=>setEditing({...editing, email:e.target.value})}/></div>
                <div><Label>Tax Rate %</Label><Input type="number" value={editing.tax_rate} onChange={e=>setEditing({...editing, tax_rate: parseFloat(e.target.value)||0})}/></div>
                <div><Label>Validity (days)</Label><Input type="number" value={editing.validity_days} onChange={e=>setEditing({...editing, validity_days: parseInt(e.target.value)||15})}/></div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2"><Label>Line items</Label><Button size="sm" variant="outline" onClick={addRow}>+ Row</Button></div>
                {editing.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center mb-2">
                    <Input className="col-span-5" placeholder="Product" value={it.product_name} onChange={e=>{ const a=[...editing.items]; a[i].product_name=e.target.value; setEditing({...editing, items:a}); }}/>
                    <Input className="col-span-2" type="number" placeholder="Qty" value={it.quantity} onChange={e=>{ const a=[...editing.items]; a[i].quantity=parseInt(e.target.value)||1; setEditing({...editing, items:a}); }}/>
                    <Input className="col-span-2" type="number" placeholder="Unit ₹" value={it.unit_price} onChange={e=>{ const a=[...editing.items]; a[i].unit_price=parseFloat(e.target.value)||0; setEditing({...editing, items:a}); }}/>
                    <Input className="col-span-2" type="number" placeholder="Disc." value={it.discount} onChange={e=>{ const a=[...editing.items]; a[i].discount=parseFloat(e.target.value)||0; setEditing({...editing, items:a}); }}/>
                    <Button size="icon" variant="ghost" className="col-span-1" onClick={()=>rmRow(i)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                  </div>
                ))}
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={editing.notes} onChange={e=>setEditing({...editing, notes:e.target.value})}/></div>
              <Button className="btn-orange w-full" onClick={create}>Create Quotation</Button>
            </div>}
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-left"><tr><th className="p-3">#</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.map(q => (
              <tr key={q.id} className="border-t border-[#E2E8F0]">
                <td className="p-3 font-mono text-xs text-[#EA580C]">{q.quote_number}</td>
                <td>{q.customer_name}</td>
                <td>{q.items?.length || 0}</td>
                <td>₹ {q.totals?.total?.toFixed(2)}</td>
                <td>{q.status}{q.emailed_at && <div className="text-[10px] text-slate-500">emailed {q.emailed_at.slice(0,10)}</div>}</td>
                <td className="whitespace-nowrap pr-2">
                  <Button size="sm" variant="outline" className="mr-1" onClick={()=>download(q.id, q.quote_number)} data-testid={`quote-pdf-${q.id}`}><Download className="w-4 h-4 mr-1"/>PDF</Button>
                  <Button size="sm" className="btn-orange" disabled={sending === q.id} onClick={()=>emailQuote(q)} data-testid={`quote-email-${q.id}`}><Mail className="w-4 h-4 mr-1"/>{sending === q.id ? "Sending..." : "Email"}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
